// Noyau — FEUILLES « CALCUL MATRICIEL » au format publié (plugin « matrix », lot M-matrices).
//
// Le juge est celui de l'ancien moteur (`packages/reasoner/src/kernel-matrix.ts` et son calcul exact
// `kernel/matrix.ts`, `kernel/exact.ts`, lots 5.8 et 6.56), transcrit en JavaScript sans changement dans
// `./matrices/` (types retirés par esbuild). Une matrice s'écrit « [1, 2 ; 3, 4] », un coefficient « (M²)₁₃ » ;
// chaque maillon d'une chaîne « = » est évalué EXACTEMENT (rationnels sur BigInt) et doit être un pas reconnu
// (remplacer, ligne par colonne, inverse 2 × 2, déterminant…) ; mêmes messages, mêmes HORA.
//
// Le juge de la feuille (judge.js) appelle `createMatrixSheet` à la création et `judgeMatrixLine` pour chaque
// ligne ; l'état du juge (mutable dans l'ancien moteur) est COPIÉ avant chaque enregistrement et rangé dans la
// ligne gardée : l'état de la feuille reste immuable.
// ============================================================================

import {
  createKMatState,
  kmatCommit,
  kmatGoalReached,
  kmatJudge,
  kmatLineAtoms,
} from './matrices/kernel-matrix.js';
import { matText } from './matrices/matrix.js';

export function createMatrixSheet(model) {
  return createKMatState(model);
}

/** Le texte de départ affiché : l'écriture du départ, mise en forme comme le moteur la relit. */
export function matrixStartText(model) {
  return matText(kmatLineAtoms(model.kernel.start));
}

const clone = (ks) => globalThis.structuredClone(ks);

export function judgeMatrixLine(ks, text) {
  const v = kmatJudge(ks, [{ atoms: kmatLineAtoms(String(text)) }], { verify: true });
  const base = { title: v.title ?? null, message: v.message ?? null };
  if (v.kind === 'step') {
    const next = clone(ks);
    kmatCommit(next, v);
    const done = kmatGoalReached(next);
    const rule = v.rule;
    const gestures = rule
      ? [{ ruleId: rule.id, label: rule.label, why: rule.why, hora: rule.hora ?? 0 }]
      : [];
    return {
      ...base,
      status: done ? 'goal' : 'step',
      kind: 'step',
      gestures,
      hora: v.hora ?? rule?.hora ?? 0,
      message:
        v.message ?? (done ? 'Tu tiens la réponse !' : rule ? `Juste : ${rule.label}.` : 'Juste.'),
      mat: next,
    };
  }
  if (v.kind === 'useless') {
    // une ligne juste gardée sans HORA (saut, rien de nouveau) : elle peut quand même fixer une forme
    const next = v.mmove ? clone(ks) : null;
    if (next) kmatCommit(next, v);
    return {
      ...base,
      status: 'useless',
      kind: 'kept',
      gestures: [],
      hora: 0,
      ...(next ? { mat: next } : {}),
    };
  }
  if (v.kind === 'unjustified')
    return { ...base, status: 'unjustified', kind: 'kept', gestures: [], hora: 0 };
  if (v.kind === 'false' || v.kind === 'trap')
    return { ...base, status: v.kind === 'trap' ? 'trap' : 'wrong', kind: null };
  // ligne vide, en cours ou illisible
  return {
    ...base,
    status: 'parse_error',
    kind: null,
    message: v.message ?? 'Termine ta ligne : écris une égalité « AB = … ».',
  };
}
